import React, { useState, useEffect } from 'react';
import './SignUp.css';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  signInWithPopup, 
  GoogleAuthProvider, 
  OAuthProvider 
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../services/firebase";  // Make sure path is correct
import Swal from 'sweetalert2';
import Navbar from '../Navbar/Navbar';
import SideImage from '../SideImg/SideImg';
import CreateAcctBanner from '../../assets/signupuserbanner.png';


const SignUp = () => {
    const [formData, setFormData] = useState({
        yourName: '',
        email: '',
        password: '',
        confirmPassword: '',
        recieveUpdates: false
    });
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState('');
    const [isLoading, setIsLoading] = useState(false);


    /* Valid Email Syntax Checker */
    const isValidEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    const evaluatePasswordStrength = (pwd) => {
        if (!pwd) return '';
        const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        const medium = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
        if (strong.test(pwd)) return 'Strong Password';
        if (medium.test(pwd)) return 'Medium Password';
        return 'Weak Password';
    };
    
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (name === 'password') {
            setPasswordStrength(evaluatePasswordStrength(value));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.yourName) newErrors.yourName = 'Name is required';
        if (!formData.email || !isValidEmail(formData.email)) newErrors.email = 'Enter a valid email';
        if (!formData.password) newErrors.password = 'Password is required';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSignUp = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        const normalizedEmail = formData.email.trim().toLowerCase();

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, formData.password);
            await sendEmailVerification(userCredential.user);
            
            // Do not store the password in Firestore!
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                name: formData.yourName,
                email: normalizedEmail,
                avatarUrl: '',
                role: "user",
                receiveUpdates: formData.recieveUpdates,
                createdAt: new Date().toISOString(),
                emailVerified: false
            });

            Swal.fire({
                title: 'Success!',
                text: 'Account created! Please check your email to verify your account.',
                icon: 'success'
            });
            // window.location.href = "/dashboard";
        
        } catch (error) {
            console.error("Full error details:", error);
            let errorMessage = "Signup failed. Please try again.";
            
            switch(error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = "This email is already registered. Sign in instead.";
                    break;
                case 'auth/weak-password':
                    errorMessage = "Your Password should be at least 6 characters.";
                    break;
                case 'auth/invalid-email':
                    errorMessage = "Please enter a valid email address.";
                    break;
                default:
                    errorMessage = error.message || "An unexpected error occurred.";
            }
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSocialSignIn = async (provider) => {
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            await setDoc(doc(db, 'users', user.uid), {
                name: user.displayName || formData.yourName || 'User',
                email: user.email,
                role: "user",
                createdAt: new Date().toISOString(),
                emailVerified: user.emailVerified
            });
            
            Swal.fire("Success", "Signed in successfully!", "success");
            
        } catch (error) {
            console.error("Social signin error:", error);
            Swal.fire("Error", error.message || "Sign-in failed", "error");
        }
    };

  return (
    <>
        <div className='page__wrapper'>
            <Navbar />
            <div className='auth__container'>
                <SideImage sideBannerSrc={CreateAcctBanner} sideBannerAlt={"Alex Menswear Create an Account"}/>
                <div className="sign-up-container">
                    <div className="sign-up-box">
                        <div className="signup-header">
                            <h1>Create Your Account</h1>
                            <p>Create your account to have access to a personalized experience.</p>
                            
                            <div className='social-login'>
                                    <button 
                                        type="button" 
                                        onClick={() => handleSocialSignIn(new GoogleAuthProvider())}   
                                        className="authOptions-btn" 
                                        disabled={isLoading}
                                    >
                                        Sign Up with Google
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => handleSocialSignIn(new OAuthProvider('apple.com'))} 
                                        className="authOptions-btn"
                                    >
                                        Sign Up with Apple
                                    </button>
                            </div>
                            <div className='sign-up-links'><p>Already have an Alex Menswear Account? <a href="sign-in">Log In here.</a></p></div>
                        </div>
                        <form className='sign-up-form' onSubmit={handleSignUp}> 
                            <div className="name-inputs">
                                <div className="form-group">
                                    <label htmlFor="yourName">Your Name</label>
                                    <input
                                        type="text"
                                        id="yourName"
                                        name='yourName'
                                        placeholder='Your Full Name'
                                        className='sign-up-input'
                                        value={formData.yourName}
                                        onChange={handleChange}
                                        required
                                    />
                                    {errors.yourName && <span className="error">{errors.yourName}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name='email'
                                    placeholder="Email Address"
                                    className='sign-up-input'
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                                {errors.email && <span className="error">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Create Your Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name='password'
                                    placeholder='Create Your New Password'
                                    className='sign-up-input'
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                <span className={`password-strength ${passwordStrength.toLowerCase().replace(' ', '-')}`}>
                                    {passwordStrength}
                                </span>
                                {errors.password && <span className="error">{errors.password}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name='confirmPassword'
                                    placeholder='Confirm Your New Password'
                                    className='sign-up-input'
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                />
                                {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
                            </div>
                
                            <div className="communication-checkboxes">
                                <div className="checkbox-group">
                                    <input
                                        type="checkbox"
                                        id="recieveUpdates"
                                        name='recieveUpdates'
                                        checked={formData.recieveUpdates}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="recieveUpdates">
                                        Receive Alex Menswear emails and communications including new releases, exclusive sales, special offers, and reviews & ratings from you.
                                    </label>
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                className="sign-up-button" 
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating Account...' : 'Create Account'}
                            </button>                           
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
}

export default SignUp;
